import { Controller } from "@hotwired/stimulus";
import { showTopBannerError, isFieldActiveAndVisible } from "../utils/form_validation_helpers";

export default class extends Controller {
  // =========================
  // TARGETS & VALUES
  // =========================
  static targets = [
    "responsibleNameInput",
    "responsiblePhoneInput",
    "tagsInput",
    "phoneInput",
    "summaryInput",
    "noCurrentSalaryCheckbox",
    "submitButton",
    "cancelButton",
  ];

  static values = {
    aiAnalysisRequired: Boolean,
    submitButtonTexts: Array,
    blankRequireds: Array,
  };

  // =========================
  // ONLINE PRESENCE RULES
  // =========================
  ONLINE_PRESENCE_RULES = {
    facebook: /facebook\.com/i,
    twitter: /(twitter|x)\.com/i,
    instagram: /instagram\.com/i,
    stackoverflow: /stackoverflow\.com/i,
    github: /github\.com/i,
    medium: /medium\.com/i,
    dribbble: /dribbble\.com/i,
    behance: /behance\.(com|net)/i,
    website:
      /^(https?:\/\/)?([\w-]+\.)+[a-z]{2,}(\/\S*)?$|^[^\s@]+@[^\s@]+\.[^\s@]+$/i,
  };

  ONLINE_PRESENCE_EXAMPLES = {
    linkedin: "Ex: https://www.linkedin.com/in/seu-nome",
    github: "Ex: https://github.com/seu-usuario",
    stackoverflow: "Ex: https://stackoverflow.com/users/123456/seu-nome",
    medium: "Ex: https://medium.com/@seu-usuario",
    dribbble: "Ex: https://dribbble.com/seu-usuario",
    behance: "Ex: https://www.behance.net/seu-usuario",
    facebook: "Ex: https://www.facebook.com/seu.perfil",
    twitter: "Ex: https://x.com/seu_usuario",
    instagram: "Ex: https://www.instagram.com/seu.usuario",
    website: "Ex: https://www.seusite.com",
  };

  tagsObserver = null;

  // =========================
  // CONNECT
  // =========================
  connect() {
    console.log("ProfileEditFormController connected");

    this.restoreDraftFromSession();
    this.clearInvalidOnlinePresence();

    this.element.addEventListener("submit", (event) => {
      // Always save draft before anything else
      this.saveDraftToSession();

      this.validateForm();

      if (!this.canSubmit()) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const firstInvalid = this.element.querySelector(".is-invalid");
        if (firstInvalid && isFieldActiveAndVisible(firstInvalid)) {
          firstInvalid.focus();
          firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
        }

        showTopBannerError("Por favor, preencha todos os campos obrigatórios corretamente antes de continuar.");
        return;
      }
    });

    this.element.addEventListener("turbo:submit-end", (event) => {
      const success = event.detail?.success;
      if (success) {
        this.clearDraftFromSession();
      }
    });

    this.validateForm({ forceOnlinePresenceUI: false });

    this.setupTagsObserver();

    setTimeout(() => {
      this.checkTags();
    }, 800);
  }

  disconnect() {
    if (this.tagsObserver) {
      this.tagsObserver.disconnect();
    }
  }

  clearInvalidOnlinePresence() {
    const inputs = this.element.querySelectorAll("[data-online-presence]");

    inputs.forEach((input) => {
      const type = input.dataset.onlinePresence;
      const value = input.value.trim();

      if (!value) return;

      let isValid = false;

      if (type === "linkedin") {
        isValid = this.isValidLinkedin(value);
      } else {
        const regex = this.ONLINE_PRESENCE_RULES[type];
        isValid = regex ? regex.test(value) : true;
      }

      if (!isValid) {
        input.value = "";
        input.classList.remove("is-invalid");
      }
    });
  }

  // =========================
  // HANDLERS
  // =========================
  handleSummaryChange() {
    this.validateForm();
  }

  handlePhoneChange() {
    this.validateForm();
  }

  handleCPFChange() {
    this.validateForm();
  }

  handleOnlinePresenceChange() {
    this.validateForm();
  }

  handleTagsChange() {
    setTimeout(() => {
      this.checkTags();
      this.validateForm();
    }, 1200);
  }

  // =========================
  // CENTRAL FLOW
  // =========================
  validateForm(options = { forceOnlinePresenceUI: true }) {
    this.checkOnlinePresence(options.forceOnlinePresenceUI);
    this.checkBlankRequireds();

    if (this.aiAnalysisRequiredValue) {
      this.checkSummary();
      this.checkTags();
    }
  }

  canSubmit() {
    if (this.aiAnalysisRequiredValue) {
      const hasSummary = this.summaryInputTarget.value.trim().length > 0;
      const hasTags = this.tagsInputTarget.value.trim().length > 0;

      if (!hasSummary || !hasTags) return false;
    }

    return this.checkBlankRequireds();
  }

  // =========================
  // VALIDATIONS
  // =========================
  checkOnlinePresence() {
    let allValid = true;
    const inputs = this.element.querySelectorAll("[data-online-presence]");

    inputs.forEach((input) => {
      const type = input.dataset.onlinePresence;
      const value = input.value.trim();

      if (!value) {
        input.classList.remove("is-invalid");
        this.removeOnlinePresenceHint(input);
        return;
      }

      let isValid = false;

      if (type === "linkedin") {
        isValid = this.isValidLinkedin(value);
      } else {
        const regex = this.ONLINE_PRESENCE_RULES[type];
        isValid = regex ? regex.test(value) : true;
      }

      if (!isValid) {
        input.classList.add("is-invalid");
        this.showOnlinePresenceHint(input, type);
        allValid = false;
      } else {
        input.classList.remove("is-invalid");
        this.removeOnlinePresenceHint(input);
      }
    });

    return allValid;
  }

  checkSummary() {
    const isFilled = this.summaryInputTarget.value.trim().length > 0;
    this.summaryInputTarget.classList.toggle("is-invalid", !isFilled);
    return isFilled;
  }

  // =========================
  // TAGS OBSERVER
  // =========================
  setupTagsObserver() {
    const tagsContainer = this.tagsInputTarget.closest(".col-12");
    if (!tagsContainer) return;

    this.tagsObserver = new MutationObserver(() => {
      this.handleTagsMutation();
    });

    this.tagsObserver.observe(tagsContainer, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  handleTagsMutation() {
    if (this.tagsObserver) this.tagsObserver.disconnect();

    this.checkTags();
    this.validateForm();

    this.setupTagsObserver();
  }

  checkTags() {
    if (!this.aiAnalysisRequiredValue) return true;

    const value = this.tagsInputTarget.value.trim();
    const isFilled = value.length > 0;

    const tokenfieldWrapper = this.element.querySelector(
      ".tokenfield, .tagify, .ts-control"
    );

    if (tokenfieldWrapper) {
      tokenfieldWrapper.style.border = isFilled ? "" : "2px solid #dc3545";
      tokenfieldWrapper.style.borderRadius = "4px";
      tokenfieldWrapper.classList.toggle("is-invalid", !isFilled);
    } else {
      this.tagsInputTarget.classList.toggle("is-invalid", !isFilled);
    }

    return isFilled;
  }

  checkBlankRequireds() {
    let allFilled = true;

    this.blankRequiredsValue.forEach((field) => {
      if (
        [
          "salary_expectation",
          "current_salary",
          "linkedin",
          "cv_or_linkedin",
        ].includes(field)
      )
        return;

      if (field === "graduation") {
        if (!this.checkGraduations()) allFilled = false;
        return;
      }

      const input = this.element.querySelector(
        `[name="ats_candidate[${field}]"], [name="candidate[${field}]"]`
      );

      if (!input || !isFieldActiveAndVisible(input)) return;

      const isFilled = input.value.trim() !== "";
      input.classList.toggle("is-invalid", !isFilled);

      if (!isFilled) allFilled = false;
    });

    return allFilled;
  }

  checkGraduations() {
    let valid = true;

    const groups = this.element.querySelectorAll(
      ".nested_fields, .nested-fields, [data-graduation]"
    );

    groups.forEach((group) => {
      if (!isFieldActiveAndVisible(group)) return;

      ["knowledge_level", "institution", "course", "start_date"].forEach(
        (field) => {
          const input = group.querySelector(`[name*="[${field}]"]`);

          if (!input || !isFieldActiveAndVisible(input)) return;

          const filled = input.value.trim() !== "";
          input.classList.toggle("is-invalid", !filled);

          if (!filled) valid = false;
        }
      );
    });

    return valid;
  }

  // =========================
  // LINKEDIN VALIDATION
  // =========================
  isValidLinkedin(value) {
    let url = value.trim();

    if (!url) return true;

    if (!/^https?:\/\//i.test(url)) {
      url = `https://${url}`;
    }

    try {
      const uri = new URL(url);

      if (!["http:", "https:"].includes(uri.protocol)) return false;

      if (!["linkedin.com", "www.linkedin.com"].includes(uri.hostname))
        return false;

      if (!/^\/in\/[^\/\s]+\/?$/i.test(uri.pathname)) return false;

      return !(uri.search || uri.hash);
    } catch {
      return false;
    }
  }

  // =========================
  // UI HELPERS
  // =========================
  showOnlinePresenceHint(input, type) {
    this.removeOnlinePresenceHint(input);

    const hint = document.createElement("div");
    hint.className = "invalid-feedback d-block";
    hint.dataset.onlinePresenceHint = true;
    hint.innerText = this.ONLINE_PRESENCE_EXAMPLES[type] || "URL inválida";

    input.parentNode.appendChild(hint);
  }

  removeOnlinePresenceHint(input) {
    const hint = input.parentNode.querySelector("[data-online-presence-hint]");
    if (hint) hint.remove();
  }

  // =========================
  // SESSION STORAGE DRAFT
  // =========================
  getDraftStorageKey() {
    const formAction = this.element.getAttribute("action") || window.location.pathname;
    return `onboarding_draft_${formAction}`;
  }

  saveDraftToSession() {
    const storage = this.getStorage();
    if (!storage) return;

    try {
      const data = {};
      const inputs = this.element.querySelectorAll("input, select, textarea");

      inputs.forEach((input) => {
        if (!input.name || input.type === "password" || input.type === "file") return;

        if (input.type === "checkbox" || input.type === "radio") {
          if (input.checked) {
            if (!data[input.name]) data[input.name] = [];
            data[input.name].push(input.value);
          }
          return;
        }

        data[input.name] = input.value;
      });

      storage.setItem(this.getDraftStorageKey(), JSON.stringify(data));
    } catch (_) {}
  }

  restoreDraftFromSession() {
    const storage = this.getStorage();
    if (!storage) return;

    try {
      const stored = storage.getItem(this.getDraftStorageKey());
      if (!stored) return;

      const data = JSON.parse(stored);
      if (!data || typeof data !== "object") return;

      Object.entries(data).forEach(([name, val]) => {
        const inputs = this.element.querySelectorAll(`[name="${name}"]`);
        if (!inputs || inputs.length === 0) return;

        inputs.forEach((input) => {
          if (input.type === "checkbox" || input.type === "radio") {
            const list = Array.isArray(val) ? val : [val];
            input.checked = list.includes(input.value);
            input.dispatchEvent(new Event("change", { bubbles: true }));
            return;
          }

          if (val !== undefined && val !== null && val !== "") {
            input.value = val;
            input.dispatchEvent(new Event("input", { bubbles: true }));
            input.dispatchEvent(new Event("change", { bubbles: true }));
          }
        });
      });
    } catch (_) {}
  }

  clearDraftFromSession() {
    const storage = this.getStorage();
    if (!storage) return;

    try {
      storage.removeItem(this.getDraftStorageKey());
    } catch (_) {}
  }

  getStorage() {
    if (typeof window === "undefined") return null;
    try {
      return window.localStorage || window.sessionStorage;
    } catch (_) {
      return null;
    }
  }
}
